import { BackButton } from 'components/BackButton/BackButton';
import { Tooltip } from 'components/Tooltip/Tooltip';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import { Button } from 'components/ui/Button/Button';
import { UserDetails } from 'components/user/UserDetails/UserDetails';
import { Amount } from 'containers/Amount/Amount';
import { useInstallments } from 'hooks/useInstallments/useInstallments';
import { useUser } from 'hooks/useUser/useUser';
import { ReactComponent as CalendarIcon } from 'icons/ico-calendar.svg';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from 'utils';
import { date } from 'utils/date/date';
import { setCaptchaResponse } from '../../../actions';
import { isLoggedIn } from '../../../selectors';
import { useDispatch } from '../../../store/configureStore';
import { RecurringInfo } from './RecurringInfo/RecurringInfo';
import './ReviewPayment.scss';

interface Props {
  onNext?: (token?: string) => void;
  onPrev?: () => void;
}

const RecurringPayment = () => {
  const i18n = useTranslations();
  const {
    chargeDay,
    currencyFrom,
    currencyTo,
    firstInstallment,
    lastInstallment,
    numberOfInstallments,
    price,
    recipient,
    recurring,
    totalAmount,
  } = useInstallments();

  return (
    <div className="ReviewPayment-Recurring">
      <dl className="Payment-details">
        <div className="Payment-summary">
          <div>
            <dt>
              <Amount value={totalAmount} currency={currencyTo.code} />
            </dt>
            <dd data-testid="recipient">
              {i18n.t('installments.to', {
                recipient: recipient.name as string,
              })}
            </dd>
          </div>
          <div className="divider"></div>
          <div>
            <dt className="flex items-center justify-start gap-2">
              <CalendarIcon aria-hidden />
              <span>
                {i18n.t('installments.duration', {
                  total: `${numberOfInstallments}`,
                })}
              </span>
            </dt>
            <dd>{i18n.t('installments.plan')}</dd>
          </div>
        </div>
        <div className="Payment-Breakdown">
          <div>
            <dt>{i18n.t('installments.number')}</dt>
            <dd data-testid="numberOfInstallments">{numberOfInstallments}</dd>
          </div>
          <div>
            <dt>{i18n.t('installments.firstPayment.amount')}</dt>
            <dd data-testid="firstInstallmentAmount">
              <Amount value={price.value} currency={currencyFrom.code} />
            </dd>
          </div>
          <div>
            <dt>{i18n.t('installments.firstPayment')}</dt>
            <dd data-testid="firstInstallmentDate">
              {date.formatToBrowserLocale(firstInstallment.date)}
            </dd>
          </div>
          {lastInstallment && (
            <div>
              <dt>{i18n.t('installments.lastPayment')}</dt>
              <dd data-testid="lastInstallmentDate">
                <div className="right-stick">
                  {date.formatToBrowserLocale(lastInstallment.date)}
                </div>
                {!chargeDay && (
                  <RecurringInfo recurring={recurring as boolean} />
                )}
              </dd>
            </div>
          )}
          {chargeDay ? (
            <div>
              <dt>{i18n.t('installments.chargeDate')}</dt>
              <dd className="charge-date-dd">
                <div className="flex items-center justify-end gap-2">
                  <Tooltip.Info as="span">
                    {i18n.t('installments.chargeDate.tooltip')}
                  </Tooltip.Info>
                  <span data-testid="chargeDate">
                    {chargeDay === -1
                      ? i18n.t('installments.chargeDate.lastDay')
                      : i18n.t('installments.chargeDate.month', {
                          chargeDay: `${date.getCardinalNumber(
                            chargeDay,
                            i18n.currentLocale,
                          )}`,
                        })}
                  </span>
                </div>
                <RecurringInfo recurring={recurring as boolean} />
              </dd>
            </div>
          ) : null}
        </div>
      </dl>
    </div>
  );
};

const ReviewPayment = ({ onNext, onPrev }: Props) => {
  const i18n = useTranslations();
  const { fullName, email } = useUser();
  const captchaRef = useRef<CaptchaRef>(null);
  const isUserLoggedIn = useSelector(isLoggedIn);
  const dispatch = useDispatch();
  const [showCaptchaError, setShowCaptchaError] = useState(false);

  const handleOnNext = async () => {
    if (captchaRef.current?.isEnabled()) {
      await captchaRef.current?.execute();
    } else {
      await onNext?.();
    }
  };
  const captchaError = (message: string) => {
    return (
      <div className="PaymentOptions-Alert" role="alert">
        <span>{message}</span>
      </div>
    );
  };

  const verifyCaptcha = async (token: string) => {
    if (token) {
      dispatch(setCaptchaResponse(token));
      await onNext?.(token);
    } else {
      setShowCaptchaError(true);
    }
  };

  return (
    <div
      className="ReviewPayment flex flex-col h-full"
      data-testid="ReviewPayment"
    >
      <div className="flex-1">
        {!isUserLoggedIn && (
          <Captcha
            ref={captchaRef}
            onVerify={verifyCaptcha}
            onError={() => setShowCaptchaError(true)}
            data-testid="captcha"
          />
        )}
        {showCaptchaError &&
          captchaError(
            i18n.t('paymentOptions.captchaVerificationError') as string,
          )}
        <div className="ReviewPayment-Header">
          <div className="ReviewPayment-Actions">
            <BackButton onClick={onPrev} className="Back" />
          </div>
          {fullName && email ? (
            <UserDetails fullName={fullName} email={email} />
          ) : null}
        </div>
        <h2 className="Heading Heading--large fontWeight-semiBold">
          {i18n.t('installments.details')}
        </h2>
        <RecurringPayment />
      </div>
      <div className="pt-6 pb-4">
        <Button
          className="uppercase"
          tone="primary"
          size="full"
          onClick={handleOnNext}
          data-testid="confirmButton"
        >
          {i18n.t('documentCollector.continueButton')}
        </Button>
      </div>
    </div>
  );
};

export { ReviewPayment };
